"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ComponentRegistry = void 0;
const tslib_1 = require("tslib");
const hoist_non_react_statics_1 = tslib_1.__importDefault(require("hoist-non-react-statics"));
const lodash_flowright_1 = tslib_1.__importDefault(require("lodash.flowright"));
const lodash_isnil_1 = tslib_1.__importDefault(require("lodash.isnil"));
const react_loadable_1 = tslib_1.__importDefault(require("react-loadable"));
const components_1 = require("../components/");
const themes_1 = require("../themes");
const utils_1 = require("../utils");
const BlueBaseModuleRegistry_1 = require("./BlueBaseModuleRegistry");
/**
 * 🎁 ComponentRegistry
 */
class ComponentRegistry extends BlueBaseModuleRegistry_1.BlueBaseModuleRegistry {
    /**
     * Resolves a Component. Wraps it in `hocs` and `styles`. Takes care of loading and error
     * states if required.
     * @param keys
     */
    resolve(...keys) {
        const item = this.findOne(...keys);
        if (!item) {
            throw Error(`Could not resolve any of the following components: [${keys.join(', ')}].`);
        }
        // If component bundle has to be downloaded, wrap into lazy
        const rawComponent = item.value.isAsync
            ? (0, react_loadable_1.default)({ loader: () => item.value, loading: components_1.ReactLoadableLoading })
            : item.value.module;
        let themedComponent = rawComponent;
        // Do we apply styles and theming to this component?
        if (item.applyStyles === true) {
            // If yes, then append applyStyles hoc
            themedComponent = (0, themes_1.applyStyles)(item.key)(rawComponent);
        }
        // HOCs
        let hocs = item.hocs;
        // Process delayed HOCs
        hocs = item.hocs.map((hoc) => Array.isArray(hoc) ? hoc[0](hoc[1]) : hoc);
        // Wrap
        const wrappedComponent = (0, lodash_flowright_1.default)([...hocs])(themedComponent);
        // Final result
        const result = (0, hoist_non_react_statics_1.default)(wrappedComponent, rawComponent);
        result.displayName = item.key;
        // Cache result
        this.setMeta(item.key, { CachedComponent: result });
        return result;
    }
    resolveFromCache(...keys) {
        for (const tempKey of keys) {
            // If it is a component, just return it as is
            if (this.isInputValue(tempKey)) {
                return tempKey;
            }
            const item = this.findOne(tempKey);
            if (typeof tempKey === 'string' && item) {
                const CachedComponent = this.getMeta(item.key, 'CachedComponent');
                if (CachedComponent) {
                    return CachedComponent;
                }
                return this.resolve(tempKey);
            }
        }
        throw Error(`Could not resolve any of the following components: [${keys.join(', ')}].`);
    }
    /**
     * Adds higher order component to the registered component
     * @param {string} key The name of the registered component to whom hocs are to added
     * @param {Array<ComponentRegistryHocItem>} hocs The HOCs to compose with the raw component.
     */
    addHocs(key, ...hocs) {
        if (!this.has(key)) {
            throw Error(`Could not add hocs for "${key}" component. Reason: Component not found.`);
        }
        const currentHocs = super.getMeta(key, 'hocs', []);
        this.setMeta(key, 'hocs', [...currentHocs, ...hocs]);
    }
    /**
     * Set styles of a component.
     * @param key
     * @param styles
     */
    setStyles(key, styles) {
        if (!this.has(key)) {
            throw Error(`Cannot set styles "${key}" component. Reason: Component not found.`);
        }
        this.setMeta(key, 'styles', styles);
    }
    /**
     * Get styles of a component
     * @param key
     */
    getStyles(key) {
        return this.getMeta(key, 'styles');
    }
    /**
     * Find one item in a given sequence. Returns the first item found.
     * @param keys
     */
    findOne(...keys) {
        for (const tempKey of keys) {
            // If it is a component, just return it as an Unknown component
            if (this.isInputValue(tempKey)) {
                return this.createItem('Unknown', { value: tempKey });
            }
            if (typeof tempKey === 'string') {
                const item = this.data.get(tempKey);
                if (!(0, lodash_isnil_1.default)(item)) {
                    return item;
                }
            }
        }
        return;
    }
    /**
     * Convert any input value to an item. This is where you transform inputs and add defualts
     * @param key
     * @param partial
     */
    createItem(key, partial) {
        const value = (0, utils_1.getDefiniteBlueBaseModule)(partial.value);
        return super.createItem(key, Object.assign(Object.assign({ applyStyles: true, hocs: [], isAsync: value.isAsync, preload: false }, partial), { value }));
    }
    /**
     * Typeguard to check a given object is an input value
     * @param value
     */
    isInputValue(value) {
        return ((0, utils_1.isBlueBaseModule)(value) ||
            typeof value === 'function' ||
            (typeof value === 'object' && typeof value.render === 'function') ||
            (typeof value === 'object' && !!value.type));
    }
}
exports.ComponentRegistry = ComponentRegistry;
